/**
 * EmailSettingsPanel — master outbound-email toggles (one switch per item).
 *
 * Lists every outbound email category grouped by area, with a switch to turn
 * each one on/off. Disabling a category stops that email type being sent
 * (gated server-side in email_service.send_email).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, AlertTriangle, KeyRound, Copy, ChevronDown, ChevronUp, Send, RefreshCw, CheckCircle2, XCircle, Clock } from "lucide-react";
import api from "../../lib/api";
import { Switch } from "../ui/switch";

const EVENT_META = {
  delivered: { cls: "text-emerald-600", Icon: CheckCircle2, label: "Delivered" },
  bounced: { cls: "text-rose-600", Icon: XCircle, label: "Bounced" },
  complained: { cls: "text-rose-600", Icon: XCircle, label: "Complained" },
  failed: { cls: "text-rose-600", Icon: XCircle, label: "Failed" },
  delivery_delayed: { cls: "text-amber-600", Icon: Clock, label: "Delayed" },
  sent: { cls: "ek-text-muted", Icon: Send, label: "Sent" },
};

const DeliverabilitySection = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/email-log", { params: { limit: 50 } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Couldn't load the email log."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (open && !data) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/email-log/refresh", null, { params: { limit: 50 } });
      toast.success(`Refreshed ${data.refreshed} email${data.refreshed === 1 ? "" : "s"} from Resend.`);
      load();
    } catch {
      toast.error("Status refresh failed.");
    } finally {
      setRefreshing(false);
    }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

  return (
    <div className="mt-10 border ek-line" data-testid="email-deliverability-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-[#F2F2F3]"
        data-testid="email-deliverability-toggle"
      >
        <span className="inline-flex items-center gap-2 text-[14px] font-medium ek-text">
          <Send size={16} className="text-[#7737F6]" /> Deliverability — recent sends
          {data && <span className="text-[12px] ek-text-muted font-normal">({data.delivered} delivered · {data.bounced} bounced)</span>}
        </span>
        {open ? <ChevronUp size={16} className="ek-text-muted" /> : <ChevronDown size={16} className="ek-text-muted" />}
      </button>

      {open && (
        <div className="p-4 border-t ek-line">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[13px] ek-text-soft">Delivery status per outbound email, pulled from Resend.</p>
            <button type="button" onClick={refresh} disabled={refreshing} className="ek-btn ek-btn-outline-dark text-[12px] px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="email-deliverability-refresh">
              <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh status"}
            </button>
          </div>
          {loading ? (
            <p className="ek-text-muted text-sm" data-testid="email-deliverability-loading">Loading…</p>
          ) : !data || data.items.length === 0 ? (
            <p className="ek-text-muted text-sm" data-testid="email-deliverability-empty">No emails sent yet.</p>
          ) : (
            <div className="border ek-line divide-y divide-zinc-100" data-testid="email-deliverability-list">
              {data.items.map((e) => {
                const meta = EVENT_META[e.last_event] || EVENT_META.sent;
                const Icon = meta.Icon;
                return (
                  <div key={e.message_id} className="flex items-center justify-between gap-4 p-3" data-testid="email-deliverability-row">
                    <div className="min-w-0">
                      <div className="text-[13.5px] ek-text truncate">{e.subject}</div>
                      <div className="text-[11.5px] ek-text-muted truncate">{e.to} · <code className="ek-surface-2 px-1">{e.category}</code> · {fmt(e.created_at)}</div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 text-[12px] font-medium shrink-0 ${meta.cls}`}>
                      <Icon size={13} /> {meta.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Auth0MagicLinkSection = () => {
  const [open, setOpen] = useState(false);
  const [tpl, setTpl] = useState(null);
  const [error, setError] = useState(false);

  const fetchTpl = () => {
    setError(false);
    api.get("/admin/email-settings/auth0-magic-link")
      .then(({ data }) => setTpl(data))
      .catch(() => { setError(true); toast.error("Couldn't load the magic-link template."); });
  };

  useEffect(() => {
    if (open && !tpl && !error) fetchTpl();
  }, [open, tpl, error]);

  const copyHtml = () => {
    if (!tpl?.html) return;
    navigator.clipboard?.writeText(tpl.html)
      .then(() => toast.success("Template HTML copied"))
      .catch(() => toast.error("Copy failed — select and copy manually."));
  };

  return (
    <div className="mt-10 border ek-line" data-testid="auth0-magic-link-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-[#F2F2F3]"
        data-testid="auth0-magic-link-toggle"
      >
        <span className="inline-flex items-center gap-2 text-[14px] font-medium ek-text">
          <KeyRound size={16} className="text-[#7737F6]" /> Auth0 magic-link branding (via Resend)
        </span>
        {open ? <ChevronUp size={16} className="ek-text-muted" /> : <ChevronDown size={16} className="ek-text-muted" />}
      </button>

      {open && (
        <div className="p-4 border-t ek-line space-y-5">
          <p className="text-[13px] ek-text-soft leading-relaxed">
            Magic-link sign-in emails are sent by Auth0. To deliver them through Resend in Ekselens
            branding, point Auth0's email provider at Resend (SMTP) and paste the branded template below.
            This is a one-time Auth0 dashboard setup.
          </p>

          {tpl ? (
            <>
              <div>
                <div className="ek-eyebrow mb-2">SMTP provider settings</div>
                <table className="text-[13px] ek-text border ek-line w-full">
                  <tbody>
                    <tr className="border-b ek-line"><td className="p-2 ek-text-muted w-40">Host</td><td className="p-2 font-mono">{tpl.smtp.host}</td></tr>
                    <tr className="border-b ek-line"><td className="p-2 ek-text-muted">Port</td><td className="p-2 font-mono">{tpl.smtp.port}</td></tr>
                    <tr className="border-b ek-line"><td className="p-2 ek-text-muted">Username</td><td className="p-2 font-mono">{tpl.smtp.username}</td></tr>
                    <tr className="border-b ek-line"><td className="p-2 ek-text-muted">Password</td><td className="p-2">{tpl.smtp.password_hint}</td></tr>
                    <tr><td className="p-2 ek-text-muted">From</td><td className="p-2 font-mono">{tpl.smtp.from}</td></tr>
                  </tbody>
                </table>
              </div>

              <div>
                <div className="ek-eyebrow mb-2">Steps</div>
                <ol className="list-decimal pl-5 space-y-1.5 text-[13px] ek-text-soft">
                  {tpl.steps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="ek-eyebrow">Branded template — subject: <span className="normal-case tracking-normal ek-text">{tpl.subject}</span></div>
                  <button type="button" onClick={copyHtml} className="ek-btn ek-btn-outline-dark text-[12px] px-3 py-1.5 inline-flex items-center gap-1.5" data-testid="auth0-magic-link-copy">
                    <Copy size={12} /> Copy HTML
                  </button>
                </div>
                <pre className="bg-[#0B0B0C] ek-text-muted text-[11px] p-3 overflow-x-auto max-h-64 whitespace-pre-wrap" data-testid="auth0-magic-link-html">{tpl.html}</pre>
              </div>
            </>
          ) : error ? (
            <div className="text-sm text-rose-600" data-testid="auth0-magic-link-error">
              Couldn't load the template.{" "}
              <button type="button" onClick={fetchTpl} className="underline hover:text-rose-700">Retry</button>
            </div>
          ) : (
            <p className="ek-text-muted text-sm">Loading template…</p>
          )}
        </div>
      )}
    </div>
  );
};

const EmailSettingsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [testing, setTesting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-settings");
      setItems(data.items || []);
    } catch {
      toast.error("Couldn't load email settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (key, enabled) => {
    setSaving(key);
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, enabled } : i)));
    try {
      await api.put(`/admin/email-settings/${key}`, { enabled });
      toast.success(`${enabled ? "Enabled" : "Disabled"} — ${key}`);
    } catch {
      toast.error("Update failed — reverting.");
      setItems((prev) => prev.map((i) => (i.key === key ? { ...i, enabled: !enabled } : i)));
    } finally {
      setSaving(null);
    }
  };

  const sendTest = async (key) => {
    setTesting(key);
    try {
      const { data } = await api.post("/admin/email-test", { category: key });
      toast.success(`Test sent to ${data.to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test send failed.");
    } finally {
      setTesting(null);
    }
  };

  const groups = items.reduce((acc, i) => {
    (acc[i.group] = acc[i.group] || []).push(i);
    return acc;
  }, {});

  const disabledCount = items.filter((i) => !i.enabled).length;

  return (
    <div data-testid="email-settings-panel">
      <div className="flex items-start gap-3 mb-6">
        <Mail className="text-[#7737F6] mt-1" size={20} />
        <div>
          <h2 className="ek-heading text-2xl ek-text">Outbound email</h2>
          <p className="ek-body text-[14px] mt-1 max-w-2xl">
            Turn individual outbound email types on or off. Disabling a switch stops that
            email being sent system-wide — useful for quiet periods, testing, or compliance holds.
          </p>
          {disabledCount > 0 && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-amber-600" data-testid="email-settings-disabled-count">
              <AlertTriangle size={13} /> {disabledCount} email type{disabledCount > 1 ? "s are" : " is"} currently disabled.
            </p>
          )}
        </div>
      </div>

      {loading ? (
        <p className="ek-text-muted text-sm" data-testid="email-settings-loading">Loading…</p>
      ) : (
        <div className="space-y-8">
          {Object.entries(groups).map(([group, rows]) => (
            <div key={group}>
              <div className="ek-eyebrow mb-3">{group}</div>
              <div className="border ek-line divide-y divide-zinc-100">
                {rows.map((row) => (
                  <div
                    key={row.key}
                    className="flex items-start justify-between gap-6 p-4"
                    data-testid={`email-setting-row-${row.key}`}
                  >
                    <div className="flex-1">
                      <div className="text-[14px] font-medium ek-text">{row.label}</div>
                      <p className="text-[12.5px] ek-text-muted mt-0.5 leading-snug">{row.description}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 pt-0.5">
                      <button
                        type="button"
                        onClick={() => sendTest(row.key)}
                        disabled={testing === row.key}
                        className="text-[11px] text-[#7737F6] hover:underline disabled:opacity-40 disabled:no-underline"
                        data-testid={`email-test-${row.key}`}
                      >
                        {testing === row.key ? "Sending…" : "Send test"}
                      </button>
                      <span className={`text-[11px] tracking-[0.12em] uppercase ${row.enabled ? "text-emerald-600" : "ek-text-muted"}`}>
                        {row.enabled ? "On" : "Off"}
                      </span>
                      <Switch
                        checked={row.enabled}
                        disabled={saving === row.key}
                        onCheckedChange={(v) => toggle(row.key, v)}
                        data-testid={`email-setting-toggle-${row.key}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Auth0MagicLinkSection />
      <DeliverabilitySection />
    </div>
  );
};

export default EmailSettingsPanel;
